import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authCheck, getStoredToken, setAuthToken } from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [authRequired, setAuthRequired] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const logout = useCallback(() => {
    setAuthToken(null);
    setIsAuthenticated(false);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const token = getStoredToken();
      if (token) setAuthToken(token);

      const { data } = await authCheck();
      const required = data?.data?.authRequired === true;
      setAuthRequired(required);

      if (!required) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(!!token);
      }
    } catch (err) {
      setAuthRequired(true);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    const onLogout = () => logout();
    window.addEventListener('auth:logout', onLogout);
    return () => window.removeEventListener('auth:logout', onLogout);
  }, [logout]);

  const loginSuccess = (token) => {
    setAuthToken(token);
    setIsAuthenticated(true);
  };

  return (
    <AuthContext.Provider
      value={{
        loading,
        authRequired,
        isAuthenticated,
        loginSuccess,
        logout,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
